import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Check, Heart, Plus, Search } from "lucide-react";
import client, { absoluteUrl } from "../api/client";
import Modal from "../Moodboard/Modal";
import { capitalise } from "../utils/text";
import { formatLength, parseLength } from "./units";

const UNITS_KEY = "jumig.units";

// The architect's preferred units, remembered on this device.
export function useUnits(initial) {
  const [units, setUnits] = useState(() => {
    if (initial) return initial;
    try {
      return localStorage.getItem(UNITS_KEY) === "m" ? "m" : "ft";
    } catch {
      return "ft";
    }
  });
  const choose = useCallback((next) => {
    setUnits(next);
    try {
      localStorage.setItem(UNITS_KEY, next);
    } catch {
      // Private browsing; the choice simply is not remembered.
    }
  }, []);
  return [units, choose];
}

export function UnitToggle({ units, onChange }) {
  return (
    <div className="ar-segmented" role="radiogroup" aria-label="Units">
      {[
        ["ft", "Feet & inches"],
        ["m", "Metres"],
      ].map(([key, label]) => (
        <button
          key={key}
          type="button"
          role="radio"
          aria-checked={units === key}
          className={units === key ? "active" : ""}
          onClick={() => onChange(key)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

// Free text in, millimetres out; shows the tidy form once the field is left.
export function LengthInput({ label, value, units, onChange, placeholder, required = false, id }) {
  const [text, setText] = useState(formatLength(value, units));
  const [invalid, setInvalid] = useState(false);
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) {
      setText(formatLength(value, units));
      setInvalid(false);
    }
  }, [value, units]);

  const commit = () => {
    focused.current = false;
    if (!text.trim()) {
      setInvalid(required);
      onChange(null);
      return;
    }
    const mm = parseLength(text, units);
    if (mm == null) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    setText(formatLength(mm, units));
    if (mm !== value) onChange(mm);
  };

  return (
    <label className={`ar-field${invalid ? " is-invalid" : ""}`} htmlFor={id}>
      <span className="ar-field-label">{label}</span>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        value={text}
        placeholder={placeholder || (units === "m" ? "e.g. 3.6 m" : "e.g. 12' 6\"")}
        onFocus={() => {
          focused.current = true;
        }}
        onChange={(event) => setText(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => event.key === "Enter" && event.currentTarget.blur()}
        aria-invalid={invalid}
      />
      {invalid && <span className="ar-field-error">Try 12' 6", 3.6 m or 3600 mm</span>}
    </label>
  );
}

export function NumberInput({ label, value, onChange, min = 0, max, step = "any", suffix, placeholder }) {
  return (
    <label className="ar-field">
      <span className="ar-field-label">{label}</span>
      <span className="ar-input-suffix">
        <input
          type="number"
          value={value ?? ""}
          min={min}
          max={max}
          step={step}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value === "" ? null : Number(event.target.value))}
        />
        {suffix && <span>{suffix}</span>}
      </span>
    </label>
  );
}

export const productName = (product) => capitalise(product?.file_name || product?.name || "") || "Untitled";

const PICKER_PAGE = 40;

// Search the catalogue (or favourites) and pick one or more products. More
// products load as the list is scrolled towards its end.
export function ProductPicker({ title = "Choose products", multiple = false, onClose, onPick }) {
  const [search, setSearch] = useState("");
  const [favourites, setFavourites] = useState(false);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const [chosen, setChosen] = useState([]);
  const gridRef = useRef(null);
  const endRef = useRef(null);
  // Answers to an older search are ignored if they arrive late.
  const request = useRef(0);

  const loadPage = useCallback(
    async (next) => {
      const id = ++request.current;
      if (next === 1) setLoading(true);
      else setLoadingMore(true);
      setFailed(false);
      try {
        const { data } = await client.get("/api/images", {
          params: { search: search || undefined, favorites: favourites ? "true" : undefined, page: next, page_size: PICKER_PAGE },
        });
        if (id !== request.current) return;
        setItems((current) => {
          const seen = new Set(next === 1 ? [] : current.map((item) => item.file_id));
          const fresh = (data.items || []).filter((item) => !seen.has(item.file_id));
          return next === 1 ? fresh : [...current, ...fresh];
        });
        setPage(next);
        setTotal(data.total || 0);
        setHasMore(Boolean(data.has_more));
      } catch {
        if (id === request.current) setFailed(true);
      } finally {
        if (id === request.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [search, favourites]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      if (gridRef.current) gridRef.current.scrollTop = 0;
      loadPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [loadPage]);

  // Watches a marker after the last product; set up again after each page so a
  // list still too short to scroll keeps filling.
  useEffect(() => {
    const root = gridRef.current;
    const end = endRef.current;
    if (!root || !end || !hasMore || loading || loadingMore || failed) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadPage(page + 1);
      },
      { root, rootMargin: "300px" }
    );
    observer.observe(end);
    return () => observer.disconnect();
  }, [items.length, hasMore, loading, loadingMore, failed, page, loadPage]);

  const toggle = (item) => {
    if (!multiple) {
      onPick([item]);
      return;
    }
    setChosen((current) =>
      current.some((entry) => entry.file_id === item.file_id)
        ? current.filter((entry) => entry.file_id !== item.file_id)
        : [...current, item]
    );
  };

  return (
    <Modal title={title} onClose={onClose} wide>
      <div className="ar-picker">
        <div className="ar-picker-bar">
          <label className="ar-search">
            <Search size={15} strokeWidth={1.8} aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name or design number"
              autoFocus
            />
          </label>
          <button
            type="button"
            className={`ar-chip${favourites ? " active" : ""}`}
            onClick={() => setFavourites((value) => !value)}
            aria-pressed={favourites}
          >
            <Heart size={14} strokeWidth={1.8} fill={favourites ? "currentColor" : "none"} aria-hidden="true" />
            Favourites
          </button>
        </div>
        {loading && !items.length ? (
          <p className="ar-muted">Loading...</p>
        ) : failed && !items.length ? (
          <p className="ar-warning">
            Could not load products.{" "}
            <button type="button" className="mb-text-button" onClick={() => loadPage(1)}>
              Try again
            </button>
          </p>
        ) : !items.length ? (
          <p className="ar-muted">{favourites ? "No favourites match." : "No products match."}</p>
        ) : (
          <div className={`ar-picker-grid${loading ? " is-refreshing" : ""}`} ref={gridRef}>
            {items.map((item) => {
              const picked = chosen.some((entry) => entry.file_id === item.file_id);
              return (
                <button
                  key={item.file_id}
                  type="button"
                  className={`ar-pick${picked ? " is-picked" : ""}`}
                  onClick={() => toggle(item)}
                  aria-pressed={multiple ? picked : undefined}
                >
                  <img src={absoluteUrl(item.thumbnail_path) || undefined} alt="" loading="lazy" />
                  <span>{productName(item)}</span>
                  <small>{capitalise(item.category)}</small>
                  {picked && (
                    <i className="ar-pick-tick">
                      <Check size={14} strokeWidth={2.4} />
                    </i>
                  )}
                </button>
              );
            })}
            <div className="ar-picker-end" ref={endRef}>
              {loadingMore ? (
                <span className="ar-muted ar-small">Loading more...</span>
              ) : failed ? (
                <button type="button" className="mb-text-button" onClick={() => loadPage(page + 1)}>
                  Could not load more. Try again
                </button>
              ) : hasMore ? (
                <button type="button" className="mb-text-button" onClick={() => loadPage(page + 1)}>
                  Load more
                </button>
              ) : null}
            </div>
          </div>
        )}
        {items.length > 0 && (
          <p className="ar-muted ar-small ar-picker-count">
            Showing {items.length} of {total} product{total === 1 ? "" : "s"}
          </p>
        )}
        {multiple && (
          <div className="mb-modal-actions">
            <button type="button" className="mb-text-button" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="mb-primary" disabled={!chosen.length} onClick={() => onPick(chosen)}>
              Add {chosen.length || ""} product{chosen.length === 1 ? "" : "s"}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}

// Adds products to an existing project or a new one. `items` are what the
// API's add-items call takes: file_id plus optional room, wall size and overrides.
export function AddToProjectDialog({ items, onClose, onAdded, defaultRoom = "" }) {
  const [projects, setProjects] = useState(null);
  const [newName, setNewName] = useState("");
  const [room, setRoom] = useState(defaultRoom);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState("");

  useEffect(() => {
    client
      .get("/api/projects")
      .then(({ data }) => setProjects(data.items || []))
      .catch(() => {
        setFailure("Could not load your projects.");
        setProjects([]);
      });
  }, []);

  const addTo = async (projectId) => {
    setBusy(true);
    setFailure("");
    try {
      const { data } = await client.post(`/api/projects/${projectId}/items`, {
        items: items.map((item) => ({ ...item, room: room.trim() || item.room || undefined })),
      });
      toast.success(
        <span>
          Added to <Link to={`/projects/${data.id}`}>{data.name}</Link>
        </span>
      );
      onAdded?.(data);
      onClose();
    } catch (err) {
      setFailure(err.response?.data?.error || "Could not add to the project.");
      setBusy(false);
    }
  };

  const createAndAdd = async (event) => {
    event.preventDefault();
    if (!newName.trim()) return;
    setBusy(true);
    try {
      const { data } = await client.post("/api/projects", { name: newName.trim() });
      await addTo(data.id);
    } catch (err) {
      setFailure(err.response?.data?.error || "Could not create the project.");
      setBusy(false);
    }
  };

  return (
    <Modal title={`Add ${items.length === 1 ? "product" : `${items.length} products`} to a project`} onClose={onClose}>
      <div className="ar-add-project">
        <label className="ar-field">
          <span className="ar-field-label">Room or wall (optional)</span>
          <input type="text" value={room} maxLength={120} onChange={(event) => setRoom(event.target.value)} placeholder="e.g. Living room – TV wall" />
        </label>
        {failure && <p className="ar-warning">{failure}</p>}
        {projects == null ? (
          <p className="ar-muted">Loading projects...</p>
        ) : (
          projects.length > 0 && (
            <ul className="ar-project-choices">
              {projects.map((project) => (
                <li key={project.id}>
                  <button type="button" onClick={() => addTo(project.id)} disabled={busy}>
                    <strong>{project.name}</strong>
                    <span>
                      {[project.client_name, `${project.item_count} product${project.item_count === 1 ? "" : "s"}`]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )
        )}
        <form className="ar-inline-form" onSubmit={createAndAdd}>
          <input
            type="text"
            value={newName}
            maxLength={120}
            onChange={(event) => setNewName(event.target.value)}
            placeholder="New project name, e.g. Mehta Residence"
            aria-label="New project name"
          />
          <button type="submit" className="mb-primary" disabled={busy || !newName.trim()}>
            <Plus size={15} strokeWidth={2} aria-hidden="true" />
            New project
          </button>
        </form>
      </div>
    </Modal>
  );
}

function fileNameFrom(disposition, fallback) {
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition || "");
  return match ? decodeURIComponent(match[1]) : fallback;
}

// Fetches a file the API builds (PDF, ZIP, image) and hands it to the browser.
// Errors come back as JSON inside the blob, so they are read out for the message.
export async function downloadFile(url, params, fallbackName) {
  try {
    const response = await client.get(url, { params, responseType: "blob", timeout: 300000 });
    const href = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = href;
    link.download = fileNameFrom(response.headers["content-disposition"], fallbackName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(href), 1000);
    return true;
  } catch (err) {
    let message = "Could not download the file.";
    try {
      const body = JSON.parse(await err.response.data.text());
      if (body.error) message = body.error;
    } catch {
      // Not JSON; keep the general message.
    }
    toast.error(message);
    return false;
  }
}

// Shows a dialog above everything, including the catalogue's image popup,
// with the studio colours wherever it is opened from.
export function Layer({ children }) {
  return createPortal(<div className="ar-tokens ar-layer">{children}</div>, document.body);
}
